import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import CheckoutBrandHeader from '../../components/public/CheckoutBrandHeader';
import { getBackendUrl } from '../../lib/env';

const API_URL = getBackendUrl();

function buildRedirectUrl(url, transaction) {
  if (!url) return '';
  const params = [];
  if (transaction?.reference_code) params.push(`ref=${encodeURIComponent(transaction.reference_code)}`);
  if (transaction?.transaction_id) params.push(`transaction_id=${encodeURIComponent(transaction.transaction_id)}`);
  if (transaction?.provider) params.push(`provider=${encodeURIComponent(transaction.provider)}`);
  if (!params.length) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}${params.join('&')}`;
}

export default function PaymentCallback() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ loading: true, status: 'pending', transaction: null, thank_you_message: '', redirect_url: '' });
  const [attempts, setAttempts] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    const ref = searchParams.get('ref') || '';
    const queryString = searchParams.toString();
    if (!ref) {
      setState({ loading: false, status: 'error', transaction: null, thank_you_message: 'Missing payment reference.', redirect_url: '' });
      return;
    }

    let mounted = true;
    const verify = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/pay/${slug}/callback?${queryString}`);
        if (!mounted) return;
        setState({
          loading: false,
          status: r.data.status || 'pending',
          transaction: r.data.transaction || null,
          thank_you_message: r.data.thank_you_message || '',
          redirect_url: r.data.redirect_url || '',
        });
        setAttempts(a => a + 1);
      } catch (e) {
        if (!mounted) return;
        setState({ loading: false, status: 'error', transaction: null, thank_you_message: e.response?.data?.detail || 'Verification failed', redirect_url: '' });
      }
    };

    verify();
    pollRef.current = setInterval(verify, 4000);

    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [slug, searchParams]);

  useEffect(() => {
    if ((state.status === 'success' || state.status === 'successful' || state.status === 'failed' || state.status === 'cancelled' || state.status === 'error') && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (attempts >= 15 && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [state.status, attempts]);

  const redirectHref = buildRedirectUrl(state.redirect_url, state.transaction);

  const isSuccess = state.status === 'success' || state.status === 'successful';
  const isPending = state.status === 'pending';
  const isFailed = state.status === 'failed' || state.status === 'cancelled' || state.status === 'error';

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
      <div className="w-full max-w-xl space-y-6">
        <CheckoutBrandHeader compact className="pt-2" />
        <Card className="w-full bg-white border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.08)] rounded-[28px] overflow-hidden">
          <CardContent className="p-8 text-center" data-testid={`payment-callback-${state.status}`}>
          {state.loading ? (
            <div className="py-8">
              <div className="w-10 h-10 mx-auto border-2 border-emerald-700 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-slate-500">Verifying your payment…</p>
            </div>
          ) : isSuccess ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Payment Successful!</h1>
              <p className="text-slate-500 mb-1">{state.thank_you_message || 'Thank you for your payment!'}</p>
              {state.transaction?.receiver_name && (
                <p className="text-sm text-slate-400 mb-4">Paid to <span className="font-medium text-slate-600">{state.transaction.receiver_name}</span></p>
              )}
            </>
          ) : isPending ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                <Clock size={32} className="text-amber-500" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Payment Pending</h1>
              <p className="text-slate-500 mb-4 text-sm">
                We're awaiting confirmation from your payment gateway. This may take a few moments — keep this page open.
              </p>
              <div className="w-6 h-6 mx-auto border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle size={32} className="text-red-500" />
              </div>
              <h1 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                {state.status === 'cancelled' ? 'Payment Cancelled' : 'Payment Not Completed'}
              </h1>
              <p className="text-slate-500 mb-4 text-sm">
                {state.thank_you_message || 'Your payment was not completed. No funds were taken — you can try again.'}
              </p>
              <Link to={`/pay/${slug}`}>
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="callback-try-again-btn">Try Again</Button>
              </Link>
            </>
          )}

          {state.transaction && (
            <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2 my-4">
              {[
                ['Amount', `${state.transaction.currency} ${state.transaction.amount?.toLocaleString()}`],
                ['Reference', state.transaction.reference_code],
                ['Transaction ID', state.transaction.transaction_id],
                ['Status', (state.transaction.status || state.status || 'pending').toString()],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-sm text-slate-500">{label}</span>
                  <span className="text-sm font-medium mono-nums">{val}</span>
                </div>
              ))}
            </div>
          )}

          {isSuccess && redirectHref && (
            <a href={redirectHref} className="text-emerald-700 text-sm font-medium hover:underline" data-testid="callback-redirect-link">
              Continue
            </a>
          )}

          <p className="text-xs text-slate-400 mt-4">Jjuma Pay</p>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
